/**
 * Landing Pages — Trackier-style CRUD list.
 *
 * Layout:
 *   - Topbar: "Landing Pages" title + Create LP button
 *   - Filter row: search (name/url) + campaign dropdown
 *   - Table: preview thumb, name, URL, campaign, status, CVR, delete
 *   - Create drawer: RHF + zod (name, url, campaign, preview_url)
 *   - Loading / empty / error states on the list
 *
 * Brand rules honored: GREEN accent only (emerald-600/700/50), zinc borders,
 * 6–8px rounding, inline SVG icons, Inter (inherited), no emoji/dark theme/
 * competitor names.
 */
import {
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	useLandingPagesList,
	useLandingPagesCreate,
	useLandingPagesDelete,
} from "./hooks";
import {
	createLandingPageSchema,
	formatCvr,
	formatStatusLabel,
	type CreateLandingPageInput,
	type LandingPageRow,
	type LandingPageStatus,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — green accent, zinc surfaces, 6–8px rounded.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgb(24_24_27/0.04)]";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-600/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const LABEL = "text-sm font-medium text-zinc-700";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"inline-flex items-center gap-1.5 rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"inline-flex items-center gap-1.5 rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"inline-flex items-center rounded-[6px] border border-zinc-200 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 hover:border-zinc-300 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Inline SVG icons (no emoji, no icon lib).
// ---------------------------------------------------------------------------

function IconPlus({ className = "h-4 w-4" }: { className?: string }) {
	return (
		<svg
			className={className}
			viewBox="0 0 20 20"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M10 4v12M4 10h12" />
		</svg>
	);
}

function IconSearch({ className = "h-4 w-4" }: { className?: string }) {
	return (
		<svg
			className={className}
			viewBox="0 0 20 20"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<circle cx="9" cy="9" r="6" />
			<path d="m17 17-3.5-3.5" />
		</svg>
	);
}

function IconExternal({ className = "h-3.5 w-3.5" }: { className?: string }) {
	return (
		<svg
			className={className}
			viewBox="0 0 20 20"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M11 3h6v6" />
			<path d="M17 3 9 11" />
			<path d="M14 11v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h5" />
		</svg>
	);
}

function IconTrash({ className = "h-3.5 w-3.5" }: { className?: string }) {
	return (
		<svg
			className={className}
			viewBox="0 0 20 20"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M3 5h14" />
			<path d="M8 5V3h4v2" />
			<path d="M5 5v12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V5" />
			<path d="M9 9v6M11 9v6" />
		</svg>
	);
}

function IconClose({ className = "h-4 w-4" }: { className?: string }) {
	return (
		<svg
			className={className}
			viewBox="0 0 20 20"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="m5 5 10 10M15 5 5 15" />
		</svg>
	);
}

function IconImage({ className = "h-5 w-5" }: { className?: string }) {
	return (
		<svg
			className={className}
			viewBox="0 0 20 20"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.75"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<rect x="3" y="4" width="14" height="12" rx="2" />
			<circle cx="8" cy="9" r="1.25" />
			<path d="m3 14 4-4 4 4 3-3 3 3" />
		</svg>
	);
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const ALL_CAMPAIGNS = "__all__";

export function LandingPagesPage() {
	const listQuery = useLandingPagesList();
	const createMut = useLandingPagesCreate();
	const deleteMut = useLandingPagesDelete();

	const [drawerOpen, setDrawerOpen] = useState(false);
	const [search, setSearch] = useState("");
	const [campaignFilter, setCampaignFilter] = useState<string>(ALL_CAMPAIGNS);

	const items: LandingPageRow[] = listQuery.data?.items ?? [];

	// Distinct campaign dropdown options (sorted, non-empty).
	const campaignOptions = useMemo<string[]>(() => {
		const seen = new Set<string>();
		for (const row of items) {
			const c = (row.campaign ?? "").trim();
			if (c) seen.add(c);
		}
		return [...seen].sort((a, b) => a.localeCompare(b));
	}, [items]);

	// Filter visible rows by search (name or url) + campaign.
	const visible = useMemo<LandingPageRow[]>(() => {
		const q = search.trim().toLowerCase();
		return items.filter((row) => {
			if (campaignFilter !== ALL_CAMPAIGNS) {
				if ((row.campaign ?? "") !== campaignFilter) return false;
			}
			if (q.length > 0) {
				const hay = `${row.name ?? ""} ${row.url ?? ""}`.toLowerCase();
				if (!hay.includes(q)) return false;
			}
			return true;
		});
	}, [items, search, campaignFilter]);

	return (
		<main
			data-testid="landing-pages-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-8"
		>
			<Topbar
				total={items.length}
				onCreate={() => setDrawerOpen(true)}
				busy={createMut.isPending}
			/>

			<FilterBar
				search={search}
				setSearch={setSearch}
				campaign={campaignFilter}
				setCampaign={setCampaignFilter}
				campaignOptions={campaignOptions}
				visibleCount={visible.length}
				totalCount={items.length}
			/>

			<TableCard
				loading={listQuery.isLoading}
				error={listQuery.error}
				rows={visible}
				totalRows={items.length}
				deletingId={
					deleteMut.isPending
						? ((deleteMut.variables as string | undefined) ?? null)
						: null
				}
				onDelete={(id) => deleteMut.mutate(id)}
				onCreate={() => setDrawerOpen(true)}
			/>

			<CreateDrawer
				open={drawerOpen}
				onClose={() => setDrawerOpen(false)}
				onSubmit={(values, done) =>
					createMut.mutate(values, {
						onSuccess: () => {
							setDrawerOpen(false);
							done();
						},
					})
				}
				busy={createMut.isPending}
				serverError={createMut.error?.message ?? null}
			/>
		</main>
	);
}

export default LandingPagesPage;

// ---------------------------------------------------------------------------
// Topbar
// ---------------------------------------------------------------------------

function Topbar(props: { total: number; onCreate: () => void; busy: boolean }) {
	return (
		<header className="flex flex-col gap-3 border-b border-zinc-200 pb-5 sm:flex-row sm:items-end sm:justify-between">
			<div className="flex flex-col gap-1">
				<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
					Landing Pages
				</p>
				<h1 className="text-brand-gradient text-2xl font-semibold tracking-tight sm:text-3xl">
					Landing Pages
				</h1>
				<p className="max-w-prose text-sm text-zinc-600">
					Manage every landing page behind your offers — search, filter by
					campaign, and launch new LPs.
				</p>
			</div>
			<div className="flex items-center gap-3">
				<span className={HINT}>{props.total} total</span>
				<button
					type="button"
					onClick={props.onCreate}
					disabled={props.busy}
					className={BTN_PRIMARY}
					data-testid="landing-pages-create-button"
				>
					<IconPlus />
					Create LP
				</button>
			</div>
		</header>
	);
}

// ---------------------------------------------------------------------------
// Filter bar
// ---------------------------------------------------------------------------

function FilterBar(props: {
	search: string;
	setSearch: (v: string) => void;
	campaign: string;
	setCampaign: (v: string) => void;
	campaignOptions: string[];
	visibleCount: number;
	totalCount: number;
}) {
	return (
		<section
			className={`${CARD} flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between`}
			aria-label="Filters"
		>
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3">
				<label className="relative block sm:w-72">
					<span className="sr-only">Search landing pages</span>
					<span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">
						<IconSearch />
					</span>
					<input
						type="search"
						placeholder="Search by name or URL."
						value={props.search}
						onChange={(e) => props.setSearch(e.currentTarget.value)}
						className={`${INPUT} pl-9`}
						data-testid="landing-pages-search"
					/>
				</label>
				<label className="sm:w-56">
					<span className="sr-only">Filter by campaign</span>
					<select
						value={props.campaign}
						onChange={(e) => props.setCampaign(e.currentTarget.value)}
						className={INPUT}
						data-testid="landing-pages-campaign-filter"
					>
						<option value={ALL_CAMPAIGNS}>All campaigns</option>
						{props.campaignOptions.map((c) => (
							<option key={c} value={c}>
								{c}
							</option>
						))}
					</select>
				</label>
			</div>
			<p className={HINT}>
				{props.visibleCount} of {props.totalCount} shown
			</p>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Table card
// ---------------------------------------------------------------------------

function TableCard(props: {
	loading: boolean;
	error: Error | null;
	rows: LandingPageRow[];
	totalRows: number;
	deletingId: string | null;
	onDelete: (id: string) => void;
	onCreate: () => void;
}) {
	let body: ReactNode;
	if (props.loading) {
		body = (
			<p
				className="p-8 text-center text-sm text-zinc-500"
				data-testid="landing-pages-loading"
			>
				Loading landing pages.
			</p>
		);
	} else if (props.error) {
		body = (
			<p
				className="p-8 text-center text-sm text-red-600"
				role="alert"
				data-testid="landing-pages-error"
			>
				{props.error.message || "Something went wrong loading landing pages."}
			</p>
		);
	} else if (props.totalRows === 0) {
		body = (
			<div
				className="flex flex-col items-center gap-3 p-10 text-center"
				data-testid="landing-pages-empty"
			>
				<div className="rounded-full border border-emerald-200 bg-emerald-50 p-3 text-emerald-600">
					<IconImage className="h-6 w-6" />
				</div>
				<div>
					<p className="text-base font-medium text-zinc-900">
						No landing pages yet
					</p>
					<p className="mt-1 text-sm text-zinc-500">
						Spin up your first LP to start tracking traffic.
					</p>
				</div>
				<button
					type="button"
					onClick={props.onCreate}
					className={BTN_PRIMARY}
					data-testid="landing-pages-empty-create"
				>
					<IconPlus />
					Create LP
				</button>
			</div>
		);
	} else if (props.rows.length === 0) {
		body = (
			<p
				className="p-8 text-center text-sm text-zinc-500"
				data-testid="landing-pages-no-matches"
			>
				No landing pages match the current filters.
			</p>
		);
	} else {
		body = (
			<div className="overflow-x-auto">
				<table
					className="min-w-full text-left text-sm"
					data-testid="landing-pages-table"
				>
					<thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
						<tr>
							<th scope="col" className="px-6 py-3 font-semibold">
								Preview
							</th>
							<th scope="col" className="px-6 py-3 font-semibold">
								Name
							</th>
							<th scope="col" className="px-6 py-3 font-semibold">
								URL
							</th>
							<th scope="col" className="px-6 py-3 font-semibold">
								Campaign
							</th>
							<th scope="col" className="px-6 py-3 font-semibold">
								Status
							</th>
							<th scope="col" className="px-6 py-3 font-semibold">
								CVR
							</th>
							<th scope="col" className="px-6 py-3 text-right font-semibold">
								<span className="sr-only">Actions</span>
							</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-zinc-200">
						{props.rows.map((row) => (
							<TableRow
								key={row.id}
								row={row}
								deleting={props.deletingId === row.id}
								onDelete={() => props.onDelete(row.id)}
							/>
						))}
					</tbody>
				</table>
			</div>
		);
	}

	return (
		<section className={`${CARD} overflow-hidden`} aria-label="Landing pages">
			{body}
		</section>
	);
}

function TableRow(props: {
	row: LandingPageRow;
	deleting: boolean;
	onDelete: () => void;
}) {
	const { row } = props;
	const status = row.status ?? null;
	return (
		<tr
			className="hover:bg-zinc-50"
			data-testid={`landing-pages-row-${row.id}`}
		>
			<td className="px-6 py-3">
				<PreviewThumb src={row.preview_url} alt={row.name} />
			</td>
			<td className="px-6 py-3 align-middle">
				<div className="font-medium text-zinc-900">{row.name}</div>
				<div className="mt-0.5 font-mono text-[11px] text-zinc-500">
					{row.id}
				</div>
			</td>
			<td className="px-6 py-3 align-middle">
				<a
					href={row.url}
					target="_blank"
					rel="noreferrer noopener"
					className="inline-flex items-center gap-1 text-sm text-emerald-700 hover:text-emerald-800 hover:underline"
				>
					<span className="max-w-[260px] truncate">{row.url}</span>
					<IconExternal />
				</a>
			</td>
			<td className="px-6 py-3 align-middle text-sm text-zinc-700">
				{row.campaign ? (
					row.campaign
				) : (
					<span className="text-zinc-400">—</span>
				)}
			</td>
			<td className="px-6 py-3 align-middle">
				<StatusBadge status={status} />
			</td>
			<td className="px-6 py-3 align-middle text-sm text-zinc-700">
				{formatCvr(row.cvr)}
			</td>
			<td className="px-6 py-3 text-right align-middle">
				<button
					type="button"
					onClick={props.onDelete}
					disabled={props.deleting}
					className={BTN_GHOST}
					data-testid={`landing-pages-delete-${row.id}`}
					aria-label={`Delete ${row.name}`}
				>
					<IconTrash />
					<span className="ml-1">
						{props.deleting ? "Deleting." : "Delete"}
					</span>
				</button>
			</td>
		</tr>
	);
}

function PreviewThumb(props: { src: string | null; alt: string }) {
	if (props.src) {
		return (
			<div className="flex h-10 w-16 items-center justify-center overflow-hidden rounded-[6px] border border-zinc-200 bg-zinc-50">
				<img
					src={props.src}
					alt={props.alt}
					className="h-full w-full object-cover"
					loading="lazy"
					onError={(e) => {
						(e.currentTarget as HTMLImageElement).style.display = "none";
					}}
				/>
			</div>
		);
	}
	return (
		<div
			className="flex h-10 w-16 items-center justify-center rounded-[6px] border border-dashed border-zinc-300 bg-zinc-50 text-zinc-400"
			aria-hidden="true"
		>
			<IconImage />
		</div>
	);
}

function StatusBadge({ status }: { status: LandingPageStatus | null }) {
	if (status === "active") {
		return (
			<span className="inline-flex items-center gap-1.5 rounded-[6px] border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
				<span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
				Active
			</span>
		);
	}
	if (status === "paused") {
		return (
			<span className="inline-flex items-center gap-1.5 rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs font-medium text-zinc-600">
				<span className="h-1.5 w-1.5 rounded-full bg-zinc-400" />
				Paused
			</span>
		);
	}
	return (
		<span className="inline-flex items-center gap-1.5 rounded-[6px] border border-zinc-200 bg-white px-2 py-0.5 text-xs font-medium text-zinc-600">
			<span className="h-1.5 w-1.5 rounded-full bg-zinc-300" />
			{formatStatusLabel(status)}
		</span>
	);
}

// ---------------------------------------------------------------------------
// Create drawer (RHF + zod)
// ---------------------------------------------------------------------------

function CreateDrawer(props: {
	open: boolean;
	onClose: () => void;
	onSubmit: (values: CreateLandingPageInput, done: () => void) => void;
	busy: boolean;
	serverError: string | null;
}) {
	const form = useForm<CreateLandingPageInput>({
		resolver: zodResolver(createLandingPageSchema),
		defaultValues: { name: "", url: "", campaign: "", preview_url: "" },
		mode: "onSubmit",
	});

	// Reset whenever the drawer is re-opened so stale inputs don't linger.
	useEffect(() => {
		if (props.open) {
			form.reset({ name: "", url: "", campaign: "", preview_url: "" });
		}
	}, [props.open, form]);

	if (!props.open) return null;

	const submit = form.handleSubmit((values) => {
		props.onSubmit(values, () => {
			form.reset({ name: "", url: "", campaign: "", preview_url: "" });
		});
	});

	const errors = form.formState.errors;

	return (
		<div
			className="fixed inset-0 z-40 flex justify-end"
			role="dialog"
			aria-modal="true"
			aria-labelledby="lp-drawer-title"
			data-testid="landing-pages-drawer"
		>
			<button
				type="button"
				aria-label="Close"
				onClick={props.onClose}
				className="absolute inset-0 bg-zinc-900/30"
				data-testid="landing-pages-drawer-backdrop"
			/>
			<aside className="relative flex h-full w-full max-w-md flex-col bg-white shadow-xl">
				<header className="flex items-start justify-between gap-4 border-b border-zinc-200 px-6 py-4">
					<div>
						<h2
							id="lp-drawer-title"
							className="text-lg font-semibold text-zinc-900"
						>
							Create landing page
						</h2>
						<p className={HINT}>
							Give your LP a name, a destination URL, and an optional campaign.
						</p>
					</div>
					<button
						type="button"
						onClick={props.onClose}
						className="rounded-[6px] p-1.5 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700"
						aria-label="Close drawer"
						data-testid="landing-pages-drawer-close"
					>
						<IconClose />
					</button>
				</header>
				<form
					onSubmit={submit}
					className="flex flex-1 flex-col gap-4 overflow-y-auto px-6 py-5"
					noValidate
					data-testid="landing-pages-drawer-form"
				>
					<div className="flex flex-col gap-1.5">
						<label htmlFor="lp-name" className={LABEL}>
							Name
						</label>
						<input
							id="lp-name"
							type="text"
							autoComplete="off"
							placeholder="Summer Flash LP"
							className={INPUT}
							aria-invalid={Boolean(errors.name)}
							{...form.register("name")}
							data-testid="landing-pages-name-input"
						/>
						{errors.name && (
							<p className={ERROR_TEXT} role="alert">
								{errors.name.message}
							</p>
						)}
					</div>
					<div className="flex flex-col gap-1.5">
						<label htmlFor="lp-url" className={LABEL}>
							Destination URL
						</label>
						<input
							id="lp-url"
							type="url"
							inputMode="url"
							autoComplete="off"
							placeholder="https://example.com/offer"
							className={INPUT}
							aria-invalid={Boolean(errors.url)}
							{...form.register("url")}
							data-testid="landing-pages-url-input"
						/>
						{errors.url && (
							<p className={ERROR_TEXT} role="alert">
								{errors.url.message}
							</p>
						)}
					</div>
					<div className="flex flex-col gap-1.5">
						<label htmlFor="lp-campaign" className={LABEL}>
							Campaign
						</label>
						<input
							id="lp-campaign"
							type="text"
							autoComplete="off"
							placeholder="e.g. summer-2026"
							className={INPUT}
							aria-invalid={Boolean(errors.campaign)}
							{...form.register("campaign")}
							data-testid="landing-pages-campaign-input"
						/>
						<p className={HINT}>Optional. Groups LPs under one campaign.</p>
						{errors.campaign && (
							<p className={ERROR_TEXT} role="alert">
								{errors.campaign.message}
							</p>
						)}
					</div>
					<div className="flex flex-col gap-1.5">
						<label htmlFor="lp-preview" className={LABEL}>
							Preview image URL
						</label>
						<input
							id="lp-preview"
							type="url"
							inputMode="url"
							autoComplete="off"
							placeholder="https://cdn.example.com/lp-hero.png"
							className={INPUT}
							aria-invalid={Boolean(errors.preview_url)}
							{...form.register("preview_url")}
							data-testid="landing-pages-preview-input"
						/>
						<p className={HINT}>
							Optional. Shown as the thumbnail in the table.
						</p>
						{errors.preview_url && (
							<p className={ERROR_TEXT} role="alert">
								{errors.preview_url.message}
							</p>
						)}
					</div>
					{props.serverError ? (
						<p
							className="rounded-[6px] border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
							role="alert"
							data-testid="landing-pages-drawer-error"
						>
							{props.serverError}
						</p>
					) : null}
				</form>
				<footer className="flex items-center justify-end gap-2 border-t border-zinc-200 px-6 py-4">
					<button
						type="button"
						onClick={props.onClose}
						className={BTN_SECONDARY}
						disabled={props.busy}
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={() => submit()}
						className={BTN_PRIMARY}
						disabled={props.busy}
						data-testid="landing-pages-submit"
					>
						{props.busy ? "Creating." : "Create landing page"}
					</button>
				</footer>
			</aside>
		</div>
	);
}
